// Client for /api/v1/whocare/articles and friends. One function per endpoint in the contract.

import api, {
  apiRequestDelete,
  apiRequestGet,
  apiRequestPatch,
  apiRequestPost,
} from 'mastodon/api';

import type {
  AccountArticle,
  Article,
  ArticleMedia,
  ArticlePublishPayload,
  ArticleReading,
  ArticleState,
  ArticleSummary,
  ArticleTranslation,
  ArticleUpdatePayload,
  Revision,
  RevisionSummary,
  TiptapDoc,
} from './types';

const BASE = 'v1/whocare/articles';

export const apiListArticles = (params: {
  state?: ArticleState;
  max_id?: string;
  limit?: number;
}) => apiRequestGet<ArticleSummary[]>(BASE, params);

export const apiCreateArticle = (data: {
  title?: string;
  subtitle?: string | null;
  body_json?: TiptapDoc;
  language?: string | null;
}) => apiRequestPost<Article>(BASE, data);

export const apiGetArticle = (id: string) =>
  apiRequestGet<Article>(`${BASE}/${id}`);

/** Rejects with an axios error; status 409 carries an `ArticleConflict` body. */
export const apiUpdateArticle = (id: string, data: ArticleUpdatePayload) =>
  apiRequestPatch<Article>(`${BASE}/${id}`, data);

export const apiDeleteArticle = (id: string) =>
  apiRequestDelete(`${BASE}/${id}`);

export const apiUploadArticleMedia = async (
  id: string,
  file: Blob,
  options: {
    description?: string;
    onUploadProgress?: (fraction: number) => void;
    signal?: AbortSignal;
  } = {},
) => {
  const data = new FormData();
  data.append('file', file);
  if (options.description) data.append('description', options.description);

  const response = await api().post<ArticleMedia>(
    `/api/${BASE}/${id}/media`,
    data,
    {
      signal: options.signal,
      onUploadProgress: ({ loaded, total }) => {
        if (options.onUploadProgress && total) {
          options.onUploadProgress(loaded / total);
        }
      },
    },
  );

  return response.data;
};

export const apiListRevisions = (id: string) =>
  apiRequestGet<RevisionSummary[]>(`${BASE}/${id}/revisions`);

export const apiGetRevision = (id: string, revisionId: string) =>
  apiRequestGet<Revision>(`${BASE}/${id}/revisions/${revisionId}`);

export const apiCreateSnapshot = (id: string, label: string) =>
  apiRequestPost<RevisionSummary>(`${BASE}/${id}/revisions`, { label });

export const apiRestoreRevision = (id: string, revisionId: string) =>
  apiRequestPost<Article>(`${BASE}/${id}/revisions/${revisionId}/restore`);

export const apiPublishArticle = (id: string, data: ArticlePublishPayload) =>
  apiRequestPost<Article>(`${BASE}/${id}/publish`, data);

export const apiResolveArticle = (id: string) =>
  apiRequestGet<{ status_id: string }>(`${BASE}/${id}/resolve`);

export const apiGetStatusArticle = (statusId: string) =>
  apiRequestGet<ArticleReading>(`v1/whocare/statuses/${statusId}/article`);

/** Rejects with an axios error: 503 when translation is not configured, 422 when nothing to translate. */
export const apiTranslateStatusArticle = (statusId: string, lang?: string) =>
  apiRequestPost<ArticleTranslation>(
    `v1/whocare/statuses/${statusId}/article/translate`,
    lang ? { lang } : undefined,
  );

export const apiListAccountArticles = (
  accountId: string,
  params: { max_id?: string; limit?: number } = {},
) =>
  apiRequestGet<AccountArticle[]>(
    `v1/whocare/accounts/${accountId}/articles`,
    params,
  );
